<template>
  <div class="pvp-root">
    <div class="pvp-content" :style="scaleStyle">
      <transition name="banner">
        <div v-if="bannerText" :key="bannerKey" class="battle-banner" :class="bannerClass">
          {{ bannerText }}
        </div>
      </transition>

      <!-- Testpanel – nur für lokale Tests, kein WS nötig -->
      <div class="test-panel">
      <button class="test-btn btn-start" @click="handleWsMessage({ cmd: 'battle_start', fighterA: { ...DEMO_A }, fighterB: { ...DEMO_B } })">
        ⚔️ battle_start
      </button>
      <button class="test-btn btn-attack-a" @click="handleWsMessage({ cmd: 'attack', attacker: 'A', damage: Math.floor(Math.random() * 30) + 10, isCrit: false, banner: `⚔️ ${fighterA.name} greift an!` })">
        🔵 attack A→B
      </button>
      <button class="test-btn btn-attack-b" @click="handleWsMessage({ cmd: 'attack', attacker: 'B', damage: Math.floor(Math.random() * 30) + 10, isCrit: false, banner: `🛡️ ${fighterB.name} kontert!` })">
        🔴 attack B→A
      </button>
      <button class="test-btn btn-end" @click="handleWsMessage({ cmd: 'battle_end', winner: 'A' })">
        🏆 battle_end
      </button>
    </div>

    <div class="arena">
      <!-- Fighter A (links) -->
      <div class="fighter-slot slot-left" :class="'state-' + fighterAState">
        <div class="fighter-name">{{ fighterA.name }}</div>
        <div class="hp-bar-wrap">
          <div class="hp-bar hp-blue" :style="{ width: hpPercent(fighterA) + '%' }"></div>
        </div>
        <div class="hp-label">{{ fighterA.hp }} / {{ fighterA.maxHp }} HP</div>
        <div class="dummy-outer">
          <div class="dummy-inner">
            <armor-dummy
              :head="fighterA.head"
              :torso="fighterA.torso"
              :legs="fighterA.legs"
              :feets="fighterA.feets"
            />
          </div>
          <transition name="dmg">
            <div
              v-if="damageA"
              :key="'da' + dmgKeyA"
              class="dmg-float"
              :class="{ 'dmg-crit': damageA.isCrit }"
            >
              {{ damageA.isCrit ? '💥' : '' }}-{{ damageA.val }}
            </div>
          </transition>
        </div>
      </div>

      <!-- Mitte -->
      <div class="center-col">
        <div class="vs-text">VS</div>
        <div v-if="round > 0" class="round-text">Runde {{ round }}</div>
      </div>

      <!-- Fighter B (rechts, gespiegelt) -->
      <div class="fighter-slot slot-right" :class="'state-' + fighterBState">
        <div class="fighter-name">{{ fighterB.name }}</div>
        <div class="hp-bar-wrap">
          <div class="hp-bar hp-red" :style="{ width: hpPercent(fighterB) + '%' }"></div>
        </div>
        <div class="hp-label">{{ fighterB.hp }} / {{ fighterB.maxHp }} HP</div>
        <div class="dummy-outer">
          <div class="dummy-inner dummy-mirror">
            <armor-dummy
              :head="fighterB.head"
              :torso="fighterB.torso"
              :legs="fighterB.legs"
              :feets="fighterB.feets"
            />
          </div>
          <transition name="dmg">
            <div
              v-if="damageB"
              :key="'db' + dmgKeyB"
              class="dmg-float"
              :class="{ 'dmg-crit': damageB.isCrit }"
            >
              {{ damageB.isCrit ? '💥' : '' }}-{{ damageB.val }}
            </div>
          </transition>
        </div>
      </div>
    </div>
    </div><!-- /pvp-content -->
  </div>
</template>

<script>
import ArmorDummy from '@/components/ArmorDummy.vue';

const DEMO_A = {
  name: 'ThorFan42',
  maxHp: 100,
  hp: 100,
  head: 'Gewebtes_Tuch_Kopf_EQ',
  torso: 'Gewebtes_Tuch_Brust_EQ',
  legs: 'Gewebtes_Tuch_Hose_EQ',
  feets: 'Gewebtes_Tuch_Schuhe_EQ',
};

const DEMO_B = {
  name: 'StreamViewer',
  maxHp: 100,
  hp: 100,
  head: 'Stoffrüstung Kopf EQ',
  torso: 'Stoffrüstung Brust EQ',
  legs: 'Stoffrüstung Hose EQ',
  feets: 'Stoffrüstung Shuhe EQ',
};

export default {
  name: 'PvpBattleOverlay',
  components: { ArmorDummy },
  data: () => ({
    active: true,
    DEMO_A,
    DEMO_B,
    fighterA: { ...DEMO_A },
    fighterB: { ...DEMO_B },
    fighterAState: 'idle',
    fighterBState: 'idle',
    damageA: null,
    damageB: null,
    dmgKeyA: 0,
    dmgKeyB: 0,
    bannerText: '',
    bannerClass: '',
    bannerKey: 0,
    round: 0,
    scale: 1,
    timers: [],
    ws: null,
  }),
  computed: {
    scaleStyle() {
      return { transform: `scale(${this.scale})` };
    },
  },
  mounted() {
    this.updateScale();
    window.addEventListener('resize', this.updateScale);
    this.runDemo();
    // WebSocket: aktivieren sobald Backend bereit ist
    // this.connectWs();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScale);
    this.active = false;
    this.timers.forEach(clearTimeout);
    this.timers = [];
    if (this.ws) this.ws.close();
  },
  methods: {
    updateScale() {
      this.scale = Math.min(window.innerWidth / 1280, window.innerHeight / 720);
    },
    hpPercent(fighter) {
      return Math.max(0, (fighter.hp / fighter.maxHp) * 100);
    },
    delay(ms) {
      return new Promise((resolve) => {
        const id = setTimeout(() => { if (this.active) resolve(); }, ms);
        this.timers.push(id);
      });
    },
    setBanner(text, cls = '') {
      this.bannerText = text;
      this.bannerClass = cls;
      this.bannerKey += 1;
    },
    async doAttack(attacker, damage, isCrit = false) {
      const def = attacker === 'A' ? 'B' : 'A';

      this[`fighter${attacker}State`] = 'attack';
      await this.delay(420);
      if (!this.active) return;

      this[`fighter${attacker}State`] = 'idle';
      this[`fighter${def}State`] = 'hit';
      this[`fighter${def}`].hp = Math.max(0, this[`fighter${def}`].hp - damage);
      this[`dmgKey${def}`] += 1;
      this[`damage${def}`] = { val: damage, isCrit };

      await this.delay(700);
      if (!this.active) return;

      this[`damage${def}`] = null;
      this[`fighter${def}State`] = this[`fighter${def}`].hp > 0 ? 'idle' : 'defeat';
    },
    runDemo() {
      this.runBattle().then(() => {
        if (this.active) this.runDemo();
      });
    },
    async runBattle() {
      this.fighterA = { ...DEMO_A };
      this.fighterB = { ...DEMO_B };
      this.fighterAState = 'idle';
      this.fighterBState = 'idle';
      this.round = 0;
      this.bannerText = '';

      this.setBanner('⚔️  Kampf startet!', 'banner-start');
      await this.delay(2200);
      if (!this.active) return;

      // Runde 1
      this.round = 1;
      this.setBanner(`⚔️  ${this.fighterA.name} greift an!`, 'banner-a');
      await this.doAttack('A', 25);
      if (!this.active) return;
      await this.delay(900);
      if (!this.active) return;

      this.setBanner(`🛡️  ${this.fighterB.name} kontert!`, 'banner-b');
      await this.doAttack('B', 18);
      if (!this.active) return;
      await this.delay(900);
      if (!this.active) return;

      // Runde 2
      this.round = 2;
      this.setBanner(`⚔️  ${this.fighterA.name} greift erneut an!`, 'banner-a');
      await this.doAttack('A', 32);
      if (!this.active) return;
      await this.delay(900);
      if (!this.active) return;

      this.setBanner(`🛡️  ${this.fighterB.name} schlägt zurück!`, 'banner-b');
      await this.doAttack('B', 22);
      if (!this.active) return;
      await this.delay(900);
      if (!this.active) return;

      // Runde 3 – Kritischer Treffer
      this.round = 3;
      this.setBanner(`💥  ${this.fighterA.name} — KRITISCHER TREFFER!`, 'banner-crit');
      await this.doAttack('A', 43, true);
      if (!this.active) return;
      await this.delay(400);
      if (!this.active) return;

      this.fighterAState = 'victory';
      this.setBanner(`🏆  ${this.fighterA.name} GEWINNT! 🏆`, 'banner-victory');
      await this.delay(5000);
    },

    // WebSocket – bereit für das Backend
    connectWs() {
      const { token } = this.$route.params;
      const url = `${this.$wsurl}/pvp/${token}`;
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({ cmd: 'init', token, clientType: 'overlay' }));
      };
      this.ws.onmessage = (e) => {
        try { this.handleWsMessage(JSON.parse(e.data)); } catch (_) { /* ignore */ }
      };
      this.ws.onclose = () => {
        if (this.active) setTimeout(() => this.connectWs(), 3000);
      };
    },
    handleWsMessage(msg) {
      switch (msg.cmd) {
        case 'battle_start':
          // { cmd: 'battle_start', fighterA: { name, maxHp, hp, head, torso, legs, feets }, fighterB: {...} }
          this.active = false;
          this.timers.forEach(clearTimeout);
          this.timers = [];
          this.fighterA = { ...msg.fighterA };
          this.fighterB = { ...msg.fighterB };
          this.fighterAState = 'idle';
          this.fighterBState = 'idle';
          this.damageA = null;
          this.damageB = null;
          this.round = 0;
          this.active = true;
          this.setBanner('⚔️  Kampf startet!', 'banner-start');
          break;
        case 'round':
          // { cmd: 'round', round: 1 }
          this.round = msg.round;
          break;
        case 'attack':
          // { cmd: 'attack', attacker: 'A'|'B', damage: 25, isCrit: false, banner: '...' }
          if (msg.banner) this.setBanner(msg.banner, msg.attacker === 'A' ? 'banner-a' : 'banner-b');
          this.doAttack(msg.attacker, msg.damage, !!msg.isCrit);
          break;
        case 'battle_end':
          // { cmd: 'battle_end', winner: 'A'|'B' }
          this[`fighter${msg.winner}State`] = 'victory';
          this.setBanner(
            `🏆  ${this[`fighter${msg.winner}`].name} GEWINNT! 🏆`,
            'banner-victory',
          );
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
.pvp-root {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 12, 22, 0.88);
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #fff;
  user-select: none;
}

/* Referenzgröße 1280×720 – wird auf jede Fenstergröße skaliert */
.pvp-content {
  position: relative;
  width: 1280px;
  height: 720px;
  flex-shrink: 0;
  transform-origin: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ── Banner ─────────────────────────────────────────── */
.battle-banner {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 40px;
  border-radius: 30px;
  font-size: 1.6rem;
  font-weight: 700;
  white-space: nowrap;
  letter-spacing: 0.4px;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.7);
  z-index: 20;
}

.banner-start   { background: linear-gradient(135deg, #1a4a8a, #2266cc); box-shadow: 0 4px 20px rgba(30,80,200,0.5); }
.banner-a       { background: linear-gradient(135deg, #1255bb, #3388ff); box-shadow: 0 4px 20px rgba(30,100,255,0.5); }
.banner-b       { background: linear-gradient(135deg, #8a1a1a, #cc3333); box-shadow: 0 4px 20px rgba(200,30,30,0.5); }
.banner-crit    { background: linear-gradient(135deg, #aa4400, #ff7700); box-shadow: 0 4px 30px rgba(255,120,0,0.7); }
.banner-victory { background: linear-gradient(135deg, #886600, #ddaa00); box-shadow: 0 4px 30px rgba(255,200,0,0.7); }

.banner-enter-active { animation: banner-in 0.35s ease-out; }
.banner-leave-active { animation: banner-out 0.3s ease-in forwards; }

@keyframes banner-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-28px) scale(0.88); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
}
@keyframes banner-out {
  from { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  to   { opacity: 0; transform: translateX(-50%) translateY(-14px) scale(0.92); }
}

/* ── Arena layout ───────────────────────────────────── */
.arena {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 80px;
  width: 100%;
  padding: 0 60px 60px;
}

/* ── Fighter slot ───────────────────────────────────── */
.fighter-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 240px;
  position: relative;
}

.fighter-name {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 8px;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
}

.hp-bar-wrap {
  width: 100%;
  height: 16px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  overflow: hidden;
  margin-bottom: 4px;
}

.hp-bar {
  height: 100%;
  border-radius: 8px;
  transition: width 0.45s ease-out;
}
.hp-blue { background: linear-gradient(90deg, #1166ee, #55aaff); }
.hp-red  { background: linear-gradient(90deg, #dd1111, #ff5555); }

.hp-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 20px;
}

/* ── Dummy container ────────────────────────────────── */
.dummy-outer {
  position: relative;
  width: 220px;
  height: 440px;
}

.dummy-inner {
  width: 100%;
  height: 100%;
}

.dummy-mirror {
  transform: scaleX(-1);
}

/* ── Damage numbers ─────────────────────────────────── */
.dmg-float {
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2.4rem;
  font-weight: 900;
  color: #ff5555;
  text-shadow: 0 0 8px rgba(255, 0, 0, 0.7), 2px 2px 4px rgba(0, 0, 0, 0.9);
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;
}

.dmg-crit {
  color: #ff9900;
  font-size: 3rem;
  text-shadow: 0 0 18px rgba(255, 140, 0, 0.9), 2px 2px 4px rgba(0, 0, 0, 0.9);
}

.dmg-enter-active {
  animation: damage-rise 0.95s ease-out forwards;
}

@keyframes damage-rise {
  0%   { opacity: 1; transform: translateX(-50%) translateY(0) scale(0.8); }
  25%  { opacity: 1; transform: translateX(-50%) translateY(-22px) scale(1.35); }
  80%  { opacity: 0.7; transform: translateX(-50%) translateY(-58px) scale(1.1); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-84px) scale(0.9); }
}

/* ── Center column ──────────────────────────────────── */
.center-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-bottom: 90px;
}

.vs-text {
  font-size: 3rem;
  font-weight: 900;
  color: #ffffff;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.35), 2px 2px 6px rgba(0, 0, 0, 0.8);
}

.round-text {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.08);
  padding: 3px 14px;
  border-radius: 10px;
  letter-spacing: 0.5px;
}

/* ── State animations ───────────────────────────────── */

/* Idle float – auf dummy-outer */
.state-idle .dummy-outer {
  animation: idle-float 2.8s ease-in-out infinite;
}

/* Angriff – lunge auf das .fighter-slot selbst (Richtung nach Seite) */
.slot-left.state-attack {
  animation: lunge-right 0.55s ease-out;
}
.slot-right.state-attack {
  animation: lunge-left 0.55s ease-out;
}

/* Treffer – shake auf dummy-outer */
.state-hit .dummy-outer {
  animation: hit-shake 0.6s ease-out;
}

/* Sieg – gold glow auf dummy-outer */
.state-victory .dummy-outer {
  animation: victory-glow 1.6s ease-in-out infinite;
}

/* Niederlage – slump auf dummy-outer */
.state-defeat .dummy-outer {
  animation: defeat-slump 0.55s ease-out forwards;
}

@keyframes idle-float {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-10px); }
}

@keyframes lunge-right {
  0%   { transform: translateX(0); }
  35%  { transform: translateX(90px); }
  65%  { transform: translateX(70px); }
  100% { transform: translateX(0); }
}

@keyframes lunge-left {
  0%   { transform: translateX(0); }
  35%  { transform: translateX(-90px); }
  65%  { transform: translateX(-70px); }
  100% { transform: translateX(0); }
}

@keyframes hit-shake {
  0%, 100% { transform: translateX(0);    filter: brightness(1); }
  12%       { transform: translateX(-13px); filter: brightness(4) sepia(1) hue-rotate(-20deg) saturate(6); }
  28%       { transform: translateX(11px);  filter: brightness(2.5); }
  44%       { transform: translateX(-8px);  filter: brightness(1.6); }
  62%       { transform: translateX(6px);   filter: brightness(1.3); }
  78%       { transform: translateX(-3px);  filter: brightness(1.1); }
}

@keyframes victory-glow {
  0%, 100% {
    filter: drop-shadow(0 0 10px gold) brightness(1);
    transform: scale(1) translateY(0);
  }
  50% {
    filter: drop-shadow(0 0 26px gold) drop-shadow(0 0 44px rgba(255, 215, 0, 0.5)) brightness(1.15);
    transform: scale(1.05) translateY(-5px);
  }
}

/* ── Testpanel ──────────────────────────────────────── */
.test-panel {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 100;
}

.test-btn {
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
  opacity: 0.85;
  transition: opacity 0.15s, transform 0.1s;
}
.test-btn:hover  { opacity: 1; transform: translateY(-2px); }
.test-btn:active { transform: translateY(0); }

.btn-start    { background: #2266cc; }
.btn-attack-a { background: #1a7ad4; }
.btn-attack-b { background: #cc2222; }
.btn-end      { background: #997700; }

@keyframes defeat-slump {
  0%   { transform: rotate(0deg) translateY(0);     filter: grayscale(0);    opacity: 1; }
  40%  { transform: rotate(-8deg) translateY(8px);  filter: grayscale(50%);  opacity: 0.8; }
  100% { transform: rotate(-18deg) translateY(24px); filter: grayscale(100%); opacity: 0.45; }
}
</style>
